import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import {
  useGetImportQuotaQuery,
  useUndoImportMutation,
} from '../../../../api/schedulingApi';
import type {
  ImportCommitResult,
  ScheduleImport,
} from '../../../../types/scheduling';
import ProvideStep from './steps/ProvideStep';
import ReviewStep from './steps/ReviewStep';
import CommitStep from './steps/CommitStep';
import ImportHistory from './ImportHistory';

interface ImportTabProps {
  scheduleId: string;
  studioId: string;
  scheduleName: string;
}

const STEPS = ['Upload', 'Review', 'Commit', 'Done'];

const ImportTab: React.FC<ImportTabProps> = ({
  scheduleId,
  studioId,
  scheduleName,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [importRow, setImportRow] = useState<ScheduleImport | null>(null);
  const [result, setResult] = useState<ImportCommitResult | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data: quota } = useGetImportQuotaQuery(
    { studioId },
    { skip: !studioId }
  );
  const [undoImport, { isLoading: isUndoing }] = useUndoImportMutation();

  const toast = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const reset = () => {
    setImportRow(null);
    setResult(null);
    setActiveStep(0);
  };

  const handleUndo = async () => {
    if (!importRow) return;
    try {
      const res = await undoImport({
        id: importRow.id,
        studioId,
        scheduleId,
      }).unwrap();
      toast(
        `Import undone${res.restored_placements ? ` — restored ${res.restored_placements} previous session(s)` : ''}.`,
        'success'
      );
      reset();
    } catch (err: any) {
      toast(err?.data?.detail || 'Could not undo the import.', 'error');
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="h6">Import a schedule</Typography>
        {quota && (
          <Chip
            variant="outlined"
            label={`AI imports: ${quota.used}/${quota.limit} this month · spreadsheet imports are free`}
          />
        )}
      </Stack>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        {activeStep === 0 && (
          <ProvideStep
            scheduleId={scheduleId}
            studioId={studioId}
            onReady={row => {
              setImportRow(row);
              setActiveStep(1);
            }}
          />
        )}

        {activeStep === 1 && importRow && (
          <ReviewStep
            importId={importRow.id}
            studioId={studioId}
            onBack={reset}
            onContinue={() => setActiveStep(2)}
          />
        )}

        {activeStep === 2 && importRow && (
          <CommitStep
            importId={importRow.id}
            studioId={studioId}
            scheduleId={scheduleId}
            scheduleName={scheduleName}
            onBack={() => setActiveStep(1)}
            onCommitted={res => {
              setResult(res);
              setActiveStep(3);
              toast('Import committed to the schedule.', 'success');
            }}
            onError={message => toast(message, 'error')}
          />
        )}

        {activeStep === 3 && result && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Imported {result.created.scheduled_classes} session(s)
              {result.created.classes
                ? `, ${result.created.classes} new class(es)`
                : ''}
              {result.created.rooms ? `, ${result.created.rooms} room(s)` : ''}
              {result.created.teachers
                ? `, ${result.created.teachers} teacher(s)`
                : ''}
              .
            </Alert>

            {result.skipped.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {result.skipped.length} session(s) skipped (duplicates or
                incomplete rows). Any new classes are listed in your Classes tab
                if you need to add them manually.
              </Alert>
            )}

            {result.conflicts?.conflict_count > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.conflicts.conflict_count} scheduling conflict warning(s)
                detected across {result.conflicts.conflicted_placements}{' '}
                session(s). These are non-blocking — review them on the grid.
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Imported classes are pinned so a later algorithm run will not move
              them. You can undo this import until you commit another one.
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                color="warning"
                variant="outlined"
                onClick={handleUndo}
                disabled={isUndoing}
              >
                {isUndoing ? 'Undoing…' : 'Undo import'}
              </Button>
              <Button onClick={reset}>Import another</Button>
            </Stack>
          </Box>
        )}
      </Paper>

      <ImportHistory scheduleId={scheduleId} studioId={studioId} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImportTab;
